import "./App.css";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { useState, useEffect, useRef, Suspense, lazy } from "react";
import { Routes, Route, useNavigate, Navigate } from "react-router-dom";

// api
import { api } from "../../utilit/Api";
import { AuthClient, ResourceClient } from "../../utilit/auth";


import { CurrentUserContext } from "../../contexts/CurrentUserContext";
import HomeRouter from "../App/HomeRouter";
import Login from "../Login/Login";
import Register from "../Register/Register";
import Сonfirmation from "../Сonfirmation/Сonfirmation";
import СontentCourse from "../CoursePage/СontentCourse/СontentCourse";
import Portfolio from "../Portfolio/Portfolio";

import RoleProtectedRoute from "../ProtectedRoute/RoleProtectedRoute";
import ActivationRoute from "../ProtectedRoute/ActivationRoute";
import Preloader from "../Preloader/Preloader";
import TopProgress from "../TopProgress/TopProgress";

import OverlayLoader from "../OverlayLoader/OverlayLoader";
import { OverlayProvider, useOverlay } from "../../contexts/OverlayContext";
import { clearAuthSession, setAuthSession } from "../../utilit/authSession";

// Lazy-loaded routes to reduce main bundle
const Admin = lazy(() => import("../AdminPage/Admin/Admin"));
const LessonPage = lazy(() => import("../LessonPage/LessonPage/LessonPage"));
const Users = lazy(() => import("../AdminPage/Users/Users"));
const Homework = lazy(() => import("../AdminPage/Homework/Homework"));
const EditCourse = lazy(() => import("../AdminPage/EditCourse/EditCourse"));
const EditLesson = lazy(() => import("../AdminPage/EditCourse/EditLesson/EditLesson"));
const PaymentReturn = lazy(() => import("../PaymentReturn/PaymentReturn"));
const NotFound = lazy(() => import("../NotFound/NotFound"));
const PrivacyPage = lazy(() => import("../LegalPages/PrivacyPage"));
const TermsPage = lazy(() => import("../LegalPages/TermsPage"));
const OfferPage = lazy(() => import("../LegalPages/OfferPage"));
const CourseFurniturePage = lazy(() => import("../Marketing/CourseFurniturePage"));
const CourseKitchenPage = lazy(() => import("../Marketing/CourseKitchenPage"));
const CourseBazisPage = lazy(() => import("../Marketing/CourseBazisPage"));

function OverlayGate() {
  const { visible } = useOverlay();
  return visible ? <OverlayLoader message="Переходим к уроку…" /> : null;
}

function App() {
  const navigate = useNavigate();
  const didInit = useRef(false);
  const isLoggingOutRef = useRef(false);

  const [currentUser, setCurrentUser] = useState(null);
  const [isLogin, setLogin] = useState(false);
  const [isReadyApp, setRedyApp] = useState(false);

  const isActivationKnown = typeof currentUser?.isActivated === "boolean";

  const [isUnit, setUnit] = useState([]);
  const [isLessonAll, setLessonAll] = useState([]);
  const [isLesson, setLesson] = useState([]);

  
  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;
    initApp();
    // eslint-disable-next-line
  }, []);

  
  async function checkAuth() {
    try {
      const res = await AuthClient.post("/refresh", null, { withCredentials: true });
      setAuthSession({
        accessToken: res.data?.accessToken,
        accessTokenExpiration: res.data?.accessTokenExpiration,
      });
      setLogin(true);
      return true;
    } catch (err) {
      clearAuthSession();
      setLogin(false);
      return false;
    }
  }

 
  async function loadInitialData() {
    try {
      const [userData, lessonAll] = await Promise.all([
        ResourceClient.get("/users/me"),
        api.getLesson(),
      ]);
      setCurrentUser(userData.data);
      setLessonAll(lessonAll);
      return true;
    } catch (err) {
      console.error("Ошибка загрузки данных:", err);
      return false;
    }
  }


  async function initApp() {
  
    try {
      const units = await api.getAllUnits();
      setUnit(units);
    } catch (err) {
      console.error("Не удалось загрузить units", err);
      setUnit([]);
    }

  
    const isAuthValid = await checkAuth();
    if (isAuthValid) {
      const isDataLoaded = await loadInitialData();
      if (!isDataLoaded) {
        setLogin(false);
        navigate("/sign-in");
      }
    }

  
    const softRefresh = async () => {
      try {
        const r = await AuthClient.post("/refresh", null, { withCredentials: true });
        if (r.data?.accessToken) {
          setAuthSession({
            accessToken: r.data.accessToken,
            accessTokenExpiration: r.data?.accessTokenExpiration,
          });
          setLogin(true);
        }
      } catch (_) {
        /* ok */
      }
    };
    const onFocus = () => softRefresh();
    window.addEventListener("focus", onFocus);
    const id = setInterval(softRefresh, 10 * 60 * 1000);

  
    setRedyApp(true);

    return () => {
      window.removeEventListener("focus", onFocus);
      clearInterval(id);
    };
  }


  const handelRegister = async (newUser) => {
    try {
      const res = await AuthClient.post("/sign-up", newUser, { withCredentials: true });
      setAuthSession({
        accessToken: res.data?.data?.accessToken || res.data?.accessToken,
        accessTokenExpiration: res.data?.accessTokenExpiration,
      });
      setLogin(true);

      const [userResp, lessonAll, units] = await Promise.all([
        ResourceClient.get("/users/me"),
        api.getLesson(),
        api.getAllUnits(),
      ]);
      setCurrentUser(userResp.data);
      setLessonAll(lessonAll);
      setUnit(units);

      navigate("/confirmation");
    } catch (err) {
      if (!err.response) {
        toast.error("Нет связи с сервером.");
      } else {
        toast.error(err.response.data.message || "Ошибка регистрации");
      }
    }
  };

 
  const handelLogin = async (dataUser) => {
    try {
      const res = await AuthClient.post("/sign-in", dataUser, { withCredentials: true });
      const { accessToken, data } = res.data;
      setAuthSession({ accessToken, accessTokenExpiration: res.data?.accessTokenExpiration, user: data });
      setLogin(true);

      setCurrentUser((prev) => ({ ...(prev || {}), isActivated: !!data.isActivated }));

      const [userResp, lessonAll, units] = await Promise.all([
        ResourceClient.get("/users/me"),
        api.getLesson(),
        api.getAllUnits(),
      ]);
      setCurrentUser(userResp.data);
      setLessonAll(lessonAll);
      setUnit(units);

      navigate(data.isActivated ? "/content" : "/confirmation");
    } catch (err) {
      if (!err.response) {
        toast.error("Нет связи с сервером.");
      } else {
        toast.error(err.response.data.message || "Ошибка входа");
      }
    }
  };

  const handleGoogleAuthError = (err) => {
    const status = err?.response?.status;
    if (!status) {
      toast.error("Нет связи с сервером.");
      return;
    }

    if (status === 401) toast.error("Google токен недействителен или истек.");
    else if (status === 403) toast.error("Google email не подтвержден.");
    else if (status === 500) toast.error("Ошибка конфигурации входа через Google.");
    else if (status === 504) toast.error("Таймаут проверки Google токена.");
    else if (status === 400) toast.error("idToken is required");
    else toast.error(err.response?.data?.message || "Ошибка входа через Google");
  };

  const handleGoogleWidgetError = (message) => {
    if (message) toast.error(message);
  };

  const handelGoogleLogin = async (credential) => {
    try {
      const res = await AuthClient.post(
        "/auth/google",
        { idToken: credential },
        { withCredentials: true }
      );
      const { data, accessToken, accessTokenExpiration } = res.data || {};

      setAuthSession({ accessToken, accessTokenExpiration, user: data });
      setLogin(Boolean(accessToken));

      const [userResp, lessonAll, units] = await Promise.all([
        ResourceClient.get("/users/me"),
        api.getLesson(),
        api.getAllUnits(),
      ]);
      setCurrentUser(userResp.data || data || null);
      setLessonAll(lessonAll);
      setUnit(units);

      navigate((userResp.data || data)?.isActivated ? "/content" : "/confirmation");
    } catch (err) {
      clearAuthSession();
      setCurrentUser(null);
      setLogin(false);
      handleGoogleAuthError(err);
    }
  };


  function exitLogin() {
    if (isLoggingOutRef.current) return;
    isLoggingOutRef.current = true;

    AuthClient.post("/logout", null, { withCredentials: true })
      .catch((err) => {
        // На logout сервер может вернуть 404, если сессия уже очищена.
        if (err?.response?.status !== 404) {
          console.error("Logout failed:", err);
        }
      })
      .finally(() => {
        clearAuthSession();
        setCurrentUser(null);
        setLogin(false);
        isLoggingOutRef.current = false;
        navigate("/sign-in");
      });
  }


  function handleAddLessonSubmit(title, markdown, markdownSpare, video, unit) {
    api
      .addLesson(title, markdown, markdownSpare, video, unit)
      .then((newLesson) => {
        setLesson((prev) => [...prev, newLesson]);
      })
      .catch((err) => alert(err));
  }

  if (!isReadyApp || (isLogin && !isActivationKnown)) {
    return (
      <>
        <TopProgress active />
        <Preloader logoText="Школа КТМ" message="Проверяем аккаунт и права доступа" />
      </>
    );
  }

  return (
    <OverlayProvider>
      <CurrentUserContext.Provider value={{ currentUser, isLogin }}>
        <div className="App">
          <OverlayGate />

          <Suspense fallback={<Preloader logoText="Загрузка" message="Подготавливаем страницы..." />}>
          <Routes>
            {/* ADMIN */}
            <Route
              path="/admin"
              element={
                <RoleProtectedRoute
                  element={<Admin />}
                  isLogin={isLogin}
                  isReadyApp={isReadyApp}
                  allowedRoles={["admin"]}
                />
              }
            >
              <Route path="users" element={<Users />} />
              <Route path="homework" element={<Homework />} />
              <Route path="edit" element={<EditCourse isUnit={isUnit} />}>
                <Route
                  path=":unit"
                  element={<EditLesson handleAddLessonSubmit={handleAddLessonSubmit} />}
                />
              </Route>
            </Route>

            {/* CONTENT ROOT */}
            <Route
              path="/content"
              element={
                <RoleProtectedRoute
                  element={
                    <СontentCourse
                      exitLogin={exitLogin}
                      isLogin={isLogin}
                      isReadyApp={isReadyApp}
                      isUnit={isUnit}
                      onLesson={isLesson}
                      isLessonAll={isLessonAll}
                    />
                  }
                  isLogin={isLogin}
                  isReadyApp={isReadyApp}
                  allowedRoles={["user", "admin"]}
                />
              }
            />

            {/* LESSON PAGE */}
            <Route
              path="/content/:courseId/lessons/:lessonId"
              element={
                <RoleProtectedRoute
                  element={
                    <LessonPage
                      onLesson={isLesson}
                      isUnit={isUnit}
                      isLessonAll={isLessonAll}
                    />
                  }
                  isLogin={isLogin}
                  isReadyApp={isReadyApp}
                  allowedRoles={["user", "admin"]}
                />
              }
            />

            {/* UNIT COMPLETE */}
            <Route
              path="/content/unit-complete"
              element={
                <RoleProtectedRoute
                  element={<LessonPage isUnit={isUnit} isLessonAll={isLessonAll} />}
                  isLogin={isLogin}
                  isReadyApp={isReadyApp}
                  allowedRoles={["user", "admin"]}
                />
              }
            />

            {/* PUBLIC */}
            <Route
              path="/"
              element={
                <HomeRouter
                  isLogin={isLogin}
                  isReadyApp={isReadyApp}
                  isUnit={isUnit}
                  exitLogin={exitLogin}
                />
                
              }
            />
            <Route path="/docs/privacy" element={<PrivacyPage />} />
            <Route path="/docs/terms" element={<TermsPage />} />
            <Route path="/docs/offer" element={<OfferPage />} />
            <Route path="/kurs-proektirovaniya-mebeli" element={<CourseFurniturePage />} />
            <Route path="/kurs-proektirovaniya-kuhon" element={<CourseKitchenPage />} />
            <Route path="/kurs-bazis-mebelshchik" element={<CourseBazisPage />} />
             
      <Route path="/portfolio" element={<Portfolio />} />
      <Route path="/portfolio/:slug" element={<Portfolio />} />
    


            {/* AUTH */}
            <Route
              path="/sign-up"
              element={
                !isLogin ? (
                  <Register
                    handelRegister={handelRegister}
                    handleGoogleLogin={handelGoogleLogin}
                    handleGoogleError={handleGoogleWidgetError}
                  />
                ) : (
                  <Navigate to="/content" replace />
                )
              }
            />
            <Route
              path="/sign-in"
              element={
                !isLogin ? (
                  <Login
                    handelLogin={handelLogin}
                    handleGoogleLogin={handelGoogleLogin}
                    handleGoogleError={handleGoogleWidgetError}
                  />
                ) : (
                  <Navigate to="/content" replace />
                )
              }
            />
            <Route
              path="/confirmation"
              element={
                <ActivationRoute
                  element={<Сonfirmation exitLogin={exitLogin} />}
                  isLogin={isLogin}
                  isReadyApp={isReadyApp}
                  
                />
              }
            />
<Route path="/payment/return" element={<PaymentReturn />} />
            {/* 404 */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>

          <ToastContainer
            position="top-center"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            pauseOnHover
          />
        </div>
      </CurrentUserContext.Provider>
    </OverlayProvider>
  );
}

export default App;


